import type { BondSummary } from '../../lib/types';
import { formatNumber } from '../../lib/format';

/**
 * Bond, drawn as hearts.
 *
 * The hearts are the whole interface. A number would be more precise and worse:
 * bond is the one system in the game that is explicitly not about optimisation,
 * and "1,240 / 3,000" invites somebody to work out the fastest route to 3,000.
 * Five hearts and a name says the only thing that matters, which is that this
 * Pokémon and you have been at it a while.
 *
 * The bar is still there for anyone who wants it, but it is secondary and
 * unlabelled by default — `showProgress` is what the detail page turns on.
 *
 * Soulbound is drawn differently on purpose. It is the end of the ladder and it
 * is meant to be recognisable across a room: the hearts go iridescent and pick
 * up a slow shimmer rather than gaining a sixth heart, because a sixth heart
 * would read as "one more to go".
 */

export function BondMeter({
  bond,
  size = 'md',
  showProgress = false,
  showName = true,
}: {
  bond: BondSummary;
  size?: 'sm' | 'md' | 'lg';
  showProgress?: boolean;
  showName?: boolean;
}) {
  const filled = bond.hearts;
  const soulbound = bond.isSoulbound;

  return (
    <div
      className={`bond bond--${size} ${soulbound ? 'bond--soulbound' : ''}`}
      style={
        {
          '--bond-base': bond.colorBase,
          '--bond-accent': bond.colorAccent,
        } as React.CSSProperties
      }
    >
      <div className="bond__hearts" role="img" aria-label={`Bond: ${bond.name}`}>
        {[0, 1, 2, 3, 4].map((index) => (
          <Heart key={index} filled={index < filled} />
        ))}
      </div>

      {showName && (
        <span className="bond__name">
          {bond.name}
          {soulbound && <span className="bond__spark" aria-hidden>✦</span>}
        </span>
      )}

      {showProgress && bond.nextLevel && (
        <div className="bond__progress">
          <div className="bond__track">
            <div className="bond__fill" style={{ width: `${Math.round(bond.ratio * 100)}%` }} />
          </div>
          <span className="tiny muted">
            {formatNumber(bond.into)} / {formatNumber(bond.needed)} to {bond.nextLevel.name}
          </span>
        </div>
      )}
      {showProgress && !bond.nextLevel && (
        <span className="tiny muted">
          {bond.certificateNo
            ? `Certificate ${bond.certificateNo}`
            : 'The furthest this bond goes.'}
        </span>
      )}
    </div>
  );
}

/**
 * One heart, as an inline path rather than a glyph.
 *
 * ♥ renders as a colour emoji on some platforms and as a hollow outline on
 * others, which is exactly the problem `components/icons.tsx` exists to avoid —
 * so the heart is a path in `currentColor` like everything else here.
 */
function Heart({ filled }: { filled: boolean }) {
  return (
    <svg
      viewBox="0 0 16 16"
      className={`bond__heart ${filled ? 'bond__heart--filled' : ''}`}
      aria-hidden
      shapeRendering="crispEdges"
    >
      <path
        d="M3 2h4v1h2V2h4v1h1v2h1v3h-1v2h-1v2h-1v1h-1v1h-1v1h-1v1H7v-1H6v-1H5v-1H4v-1H3v-2H2V8H1V5h1V3h1z"
        fill="currentColor"
      />
    </svg>
  );
}

/**
 * The compact form: filled hearts only, for a card corner or a Grove panel.
 *
 * Renders nothing at level 0. A row of five empty hearts on every card in a
 * collection of four hundred is visual noise that says "you have failed at
 * something you did not know you were doing".
 */
export function BondPips({ bond }: { bond: BondSummary }) {
  if (bond.level === 0) return null;
  return (
    <span
      className={`bond-pips ${bond.isSoulbound ? 'bond-pips--soulbound' : ''}`}
      title={`Bond: ${bond.name}`}
      style={{ '--bond-base': bond.colorBase } as React.CSSProperties}
    >
      {Array.from({ length: bond.hearts }, (_, index) => (
        <Heart key={index} filled />
      ))}
    </span>
  );
}
